import { useEffect, useRef } from 'react';
import { t } from '../i18n/helpers';
import { useToast } from '../components/shared/ToastContext';
import { GameDisplayRoomState } from '../types/game';

/**
 * Hook to track player connections/disconnections and show toast notifications.
 * Uses player IDs (not names) so a new joiner with a different name is never
 * mistaken for someone who intentionally left.
 */
export const usePlayerTracking = (
  players: GameDisplayRoomState['players'],
  currentQuestion: { questionId?: string; id?: string } | null
) => {
  const { showToast } = useToast();
  const previousPlayersRef = useRef<Array<{ id: string; name: string; score: number; avatar?: string }>>([]);
  const playersWhoLeftRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const previous = previousPlayersRef.current;
    const currentIds = new Set(players.map((p) => p.id));

    // Players who disappeared from standings (intentional leave or roster sync)
    if (previous.length > 0) {
      previous
        .filter((p) => !currentIds.has(p.id))
        .forEach((leftPlayer) => {
          showToast(t('toasts.playerLeft', { name: leftPlayer.name }), 'warning', 3000);
          playersWhoLeftRef.current.add(leftPlayer.id);
        });
    }

    // Same player ID returned after leaving (disconnect tab refresh, or re-join same name)
    players
      .filter((p) => playersWhoLeftRef.current.has(p.id) && !previous.some((prev) => prev.id === p.id))
      .forEach((rejoinedPlayer) => {
        showToast(t('toasts.playerReconnected', { name: rejoinedPlayer.name }), 'success', 3000);
        playersWhoLeftRef.current.delete(rejoinedPlayer.id);
      });

    previousPlayersRef.current = players;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, currentQuestion]);
};
